import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { RiskRuleEditor } from '@/components/shared/RiskRuleEditor';
import { useBreadcrumbLabel } from '@/hooks/useBreadcrumbLabel';
import {
  ruleTemplateQueryOptions,
  useRuleTemplate,
} from '@/hooks/useRuleTemplate';
import { queryClient } from '@/lib/utils/queryClient';

export const Route = createFileRoute(
  '/management/risk-management/(pages)/rule-templates/$id/edit/',
)({
  // Warms the template so the editor renders its fields rather than a skeleton.
  // Swallowed on failure: the editor's own query surfaces the error in place,
  // where the router's error boundary would replace the page instead.
  loader: ({ params }) =>
    queryClient
      .ensureQueryData(ruleTemplateQueryOptions(params.id))
      .catch(() => undefined),
  component: ManagementRuleEditPage,
});

/**
 * The same editor the client rules page hosts, at the management URL the old
 * dashboard used. Both places it can navigate away to are management's own.
 */
function ManagementRuleEditPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  // The crumb for this segment is the template's id otherwise. The loader has
  // already put the template in cache, so this costs no request.
  const { template } = useRuleTemplate(id);
  useBreadcrumbLabel(id, template.data?.title);

  const backToList = () =>
    void navigate({
      to: '/management/risk-management',
      search: { tab: 'rule-templates' },
    });

  return (
    <RiskRuleEditor
      id={id}
      // Retraced staff edit the catalogue itself: the templates are not their
      // company's, and the published ones are the ones worth correcting.
      enforceOwnership={false}
      onLeaveNotEditable={backToList}
      onCloned={backToList}
    />
  );
}
